import React from 'react'
import injectSheet from 'react-jss'
import { Icon } from 'aria-viewer/common/components/Icon'
import compose from 'recompose/compose'
import pure from 'recompose/pure'
import classNames from 'classnames'
import withHandlers from 'recompose/withHandlers'
import { MEDIA_TYPES } from 'aria-viewer/tour'

const styleSheet = theme => ({
    hotspotModal: {
        position: 'absolute',
        top: '0',
        bottom: '0',
        left: '0',
        transform: 'translate3d(0px, 0px, 2px)',
        zIndex: '3000',
        backgroundColor: 'rgba(0,0,0,0.8)',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        // ENG-69 && ENG-71
        '&::-webkit-scrollbar': {
            // using webkit scrollbar will disable the mobile accelerator
            display: 'none',
        },
        overflow: '-moz-scrollbars-none',
        '-ms-overflow-style': 'none',
        // Currently disabled
        // overflowY: ({ type, isMobile }) =>
        //     type === MEDIA_TYPES.panorama || (isMobile && type === MEDIA_TYPES.video360) ? 'hidden' : 'scroll',
        padding: ({ type, isMobile }) => {
            if (type === MEDIA_TYPES.panorama && !isMobile) {
                return '32px'
            } else {
                return null
            }
        },
        pointerEvents: 'all',
        cursor: 'pointer',

        '& *:not(.vjs-icon-placeholder, .material-icons)': {
            fontFamily: 'Circular Std Book !important',
        },
        '& .vjs-load-progress': {
            backgroundColor: '#ffffff !important',
        },
        '& .vjs-play-progress': {
            backgroundColor: '#4a90e2 !important',
        },
        '& .vjs-slider': {
            backgroundColor: 'rgba(255, 255, 255, 0.4) !important',
        },
        '& .vjs-volume-panel': {
            position: 'static !important',
            marginTop: '0px !important',
        },
        '& .vjs-scene-return-button': {
            display: 'none',
        },
        [theme.breakpoints.down('xs')]: {
            padding: '0',
            right: 0,
            backgroundColor: ({ type }) => (type === MEDIA_TYPES.photo ? 'rgba(0,0,0,0.9)' : 'rgba(0,0,0,1)'),
        },
        width: ({ viewportWidth }) => viewportWidth,
    },
    hotspotModalCloseButton: {
        position: 'absolute',
        verticalAlign: 'top',
        right: '48px',
        top: '48px',
        cursor: 'pointer',
        zIndex: '3008',
        transform: 'translate3d(0px, 0px, 2px)',
        zoom: 1,
        pointerEvents: 'all',
        color: theme.palette.common.white,
        width: '30px',
        height: '30px',
        backgroundColor: 'rgba(0, 0, 0, 0.4)',
        borderRadius: '50%',
        paddingLeft: '6px',
        paddingTop: '6px',
        '&.panorama': {
            marginRight: ({ type, isMobile }) => {
                return !(type === MEDIA_TYPES.panorama && !isMobile) ? '8px' : null
            },
        },
        [theme.breakpoints.down('xs')]: {
            right: '16px',
            top: '16px',
        },
    },
    hotspotModalContent: {
        zIndex: '2004',
        pointerEvents: 'all',
        cursor: 'default',
        overflowY: 'auto',
        // ENG-69 && ENG-71
        // https://www.geeksforgeeks.org/hide-scroll-bar-but-while-still-being-able-to-scroll-using-css/
        '&::-webkit-scrollbar': {
            // using webkit scrollbar will disable the mobile accelerator
            display: 'none',
        },
        overflow: '-moz-scrollbars-none',
        '-ms-overflow-style': 'none',
        position: ({ isMobile, isTablet }) => (isMobile && !isTablet ? 'fixed' : 'relative'),
        '&.photo': {
            width: '920px',
            height: ({ isMobile }) => {
                return isMobile ? '100%' : null
            },
            marginTop: ({ isMobile, isLandscape }) => {
                return isMobile && !isLandscape ? '95px' : null
            },
            maxHeight: '100%',
            [theme.breakpoints.down('1024')]: {
                width: '664px',
            },
            [theme.breakpoints.down('xs')]: {
                width: '100%',
            },
        },
        '&.video': {
            width: '100%',
            height: '100%',
        },
        '&.panorama': {
            width: ({ isMobile, viewportWidth }) => (isMobile ? '100%' : viewportWidth),
            height: '100%',
            maxHeight: ({ type }) => (!(type === MEDIA_TYPES.panorama) ? '800px' : null),
            '& > div': {
                height: '100%',
            },
            '& > div > div': {
                width: ({ isMobile, viewportWidth }) => (!isMobile ? viewportWidth - 65 : null),
                height: '100%',
            },
            '& > div .panoTitleDescriptionContainer': {
                height: 'auto',
                padding: '0 24px',
                paddingBottom: ({ isMobile }) => (isMobile ? '5px' : '40px'),
                position: 'absolute',
                bottom: '0',
            },
        },
    },
    '@keyframes fadein': {
        '0%': {
            opacity: 0,
        },
        '100%': {
            opacity: 1,
        },
    },
    '@keyframes expandFrontCircle': {
        '0%': {
            width: '40px',
            height: '40px',
        },
        '100%': {
            width: ({ viewportHeight }) => `${viewportHeight * 2 + 50}px`,
            height: ({ viewportHeight }) => `${viewportHeight * 2 + 50}px`,
        },
    },
    '@keyframes expandBackCircle': {
        '0%': {
            width: '50px',
            height: '50px',
        },
        '100%': {
            width: ({ viewportHeight }) => `${viewportHeight * 2 + 50}px`,
            height: ({ viewportHeight }) => `${viewportHeight * 2 + 50}px`,
        },
    },
    '@keyframes showCircle': {
        '0%': {
            opacity: 0,
        },
        '20%': {
            opacity: 0.5,
        },
        '100%': {
            opacity: 0.5,
        },
    },
    '@keyframes moveCircle': {
        '0%': {
            top: ({ hotspotCoordinates }) => `${hotspotCoordinates.y - 20}px`,
            left: ({ hotspotCoordinates }) => `${hotspotCoordinates.x - 20}px`,
        },
        '100%': {
            left: ({ viewportWidth }) => `${viewportWidth / 2}px`,
            top: ({ viewportHeight }) => `${viewportHeight / 2}px`,
        },
    },
    '@keyframes removeCircle': {
        '0%': {
            opacity: 1,
        },
        '100%': {
            opacity: 0,
        },
    },
    hotspot: {
        top: 0,
        animation: '1s linear 1s fadein',
        animationFillMode: 'forwards',
        opacity: 0,
        width: ({ viewportWidth }) => `${viewportWidth}px`,
        height: ({ viewportHeight }) => `${viewportHeight}px`,
        position: 'absolute',
        zIndex: 5003,
    },
    hotspot3DVideo: {
        top: 0,
        opacity: 1,
        width: ({ viewportWidth }) => `${viewportWidth}px`,
        height: ({ viewportHeight }) => `${viewportHeight}px`,
        position: 'absolute',
        zIndex: 5003,
    },
    maskingSquare: {
        top: 0,
        width: ({ viewportWidth }) => `${viewportWidth}px`,
        height: ({ viewportHeight }) => `${viewportHeight}px`,
        position: 'absolute',
        overflow: 'hidden',
    },
    backCircle: {
        animation: '0.5s linear 0.5s showCircle, 0.5s linear 0.5s expandBackCircle, 1s linear 1.25s removeCircle',
        animationFillMode: 'forwards',
        width: '50px',
        height: '50px',
        opacity: 0,
        borderRadius: '50%',
        backgroundColor: 'white',
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 5001,
    },
    frontCircle: {
        animation: '0.25s linear 0s moveCircle, 0.5s linear 0.5s expandFrontCircle, 1s linear 1.25s removeCircle',
        animationFillMode: 'forwards',
        width: '40px',
        height: '40px',
        borderRadius: '50%',
        backgroundColor: 'white',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        position: 'absolute',
        zIndex: 5002,
    },
    hotspotModalContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        width: '300px',
        margin: 'auto',
    },
})

const style = injectSheet(styleSheet)

const handlers = withHandlers({
    onClickStopPropagation: _ => event => event.stopPropagation(),
})

const HotspotModal = ({ classes, type, onClose, Player, onClickStopPropagation, isMobile, hotspotCoordinates }) => {
    return (
        <div className={isMobile ? classes.hotspotModalContainer : ''}>
            {isMobile && hotspotCoordinates && type !== '360 video' && (
                <div className={classes.maskingSquare}>
                    <div className={classes.frontCircle} />
                    <div className={classes.backCircle} />
                </div>
            )}
            <div className={isMobile ? (type === '360 video' ? classes.hotspot3DVideo : classes.hotspot) : ''}>
                <div className={classNames(classes.hotspotModalCloseButton, type)} onClick={onClose}>
                    <Icon>clear</Icon>
                </div>
                <div className={classes.hotspotModal} onClick={onClose}>
                    <div className={classNames(classes.hotspotModalContent, type)} onClick={onClickStopPropagation}>
                        {Player()}
                    </div>
                </div>
            </div>
        </div>
    )
}

export default compose(
    style,
    handlers,
    pure
)(HotspotModal)
